import { supabase } from '../lib/supabase';

// Luhn Algorithm for card number validation
const luhnCheck = (cardNumber) => {
  const digits = cardNumber?.replace(/\D/g, '');
  
  if (digits?.length < 13 || digits?.length > 19) {
    return false;
  }
  
  let sum = 0;
  let isEven = false;
  
  for (let i = digits?.length - 1; i >= 0; i--) {
    let digit = parseInt(digits?.[i], 10);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  return sum % 10 === 0;
};

// Detect card brand from card number
const detectCardBrand = (cardNumber) => {
  const digits = cardNumber?.replace(/\D/g, '');
  
  if (/^4/?.test(digits)) return 'Visa';
  if (/^5[1-5]/?.test(digits)) return 'Mastercard';
  if (/^3[47]/?.test(digits)) return 'American Express';
  if (/^6(?:011|5)/?.test(digits)) return 'Discover';
  
  return 'Unknown';
};

export const linkedCardService = {
  // Verify card authenticity using Luhn algorithm
  verifyCard(cardNumber) {
    const isValid = luhnCheck(cardNumber);
    const brand = detectCardBrand(cardNumber);
    
    return {
      isValid,
      brand,
      message: isValid ? 'Card number is valid' : 'Invalid card number'
    };
  },

  // Get all linked cards for current user
  async getAll(userId) {
    try {
      const { data, error } = await supabase?.from('linked_cards')?.select('*')?.eq('user_id', userId)?.order('position', { ascending: true });
      
      if (error) throw error;
      
      // Convert snake_case to camelCase
      return data?.map(card => ({
        id: card?.id,
        userId: card?.user_id,
        cardName: card?.card_name,
        cardType: card?.card_type,
        cardNumberLastFour: card?.card_number_last_four,
        cardHolderName: card?.card_holder_name,
        cardBrand: card?.card_brand,
        expiryMonth: card?.expiry_month,
        expiryYear: card?.expiry_year,
        verificationStatus: card?.verification_status,
        isActive: card?.is_active,
        position: card?.position,
        createdAt: card?.created_at,
        updatedAt: card?.updated_at
      })) || [];
    } catch (error) {
      throw new Error(error?.message || 'Failed to fetch linked cards');
    }
  },

  // Add new linked card
  async add(cardData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify card using Luhn algorithm
      const verification = this.verifyCard(cardData?.cardNumber);
      
      if (!verification?.isValid) {
        throw new Error('Invalid card number. Please check and try again.');
      }

      // Get last 4 digits
      const lastFour = cardData?.cardNumber?.replace(/\D/g, '')?.slice(-4);

      // Get current card count
      const { data: existingCards } = await supabase?.from('linked_cards')?.select('id')?.eq('user_id', user?.id);

      if (existingCards && existingCards?.length >= 3) {
        throw new Error('Maximum of 3 cards allowed. Please remove a card before adding a new one.');
      }

      // Calculate next position
      const nextPosition = (existingCards?.length || 0) + 1;

      // Convert camelCase to snake_case for database
      const { data, error } = await supabase?.from('linked_cards')?.insert({
          user_id: user?.id,
          card_name: cardData?.cardName,
          card_type: cardData?.cardType,
          card_number_last_four: lastFour,
          card_holder_name: cardData?.cardHolderName,
          card_brand: verification?.brand,
          expiry_month: cardData?.expiryMonth,
          expiry_year: cardData?.expiryYear,
          verification_status: 'verified',
          is_active: existingCards?.length === 0, // First card is active by default
          position: nextPosition
        })?.select()?.single();
      
      if (error) throw error;
      
      // Convert snake_case to camelCase
      return {
        id: data?.id,
        userId: data?.user_id,
        cardName: data?.card_name,
        cardType: data?.card_type,
        cardNumberLastFour: data?.card_number_last_four,
        cardHolderName: data?.card_holder_name,
        cardBrand: data?.card_brand,
        expiryMonth: data?.expiry_month,
        expiryYear: data?.expiry_year,
        verificationStatus: data?.verification_status,
        isActive: data?.is_active,
        position: data?.position,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      throw new Error(error?.message || 'Failed to add card');
    }
  },

  // Set active card (swap between cards)
  async setActive(cardId, userId) {
    try {
      // Deactivate all cards first
      await supabase?.from('linked_cards')?.update({ is_active: false })?.eq('user_id', userId);

      // Activate selected card
      const { data, error } = await supabase?.from('linked_cards')?.update({ is_active: true })?.eq('id', cardId)?.eq('user_id', userId)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        userId: data?.user_id,
        cardName: data?.card_name,
        cardType: data?.card_type,
        cardNumberLastFour: data?.card_number_last_four,
        cardHolderName: data?.card_holder_name,
        cardBrand: data?.card_brand,
        expiryMonth: data?.expiry_month,
        expiryYear: data?.expiry_year,
        verificationStatus: data?.verification_status,
        isActive: data?.is_active,
        position: data?.position,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      throw new Error(error?.message || 'Failed to set active card');
    }
  },

  // Delete linked card
  async delete(cardId, userId) {
    try {
      const { error } = await supabase?.from('linked_cards')?.delete()?.eq('id', cardId)?.eq('user_id', userId);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      throw new Error(error?.message || 'Failed to delete card');
    }
  },

  // Update card position (for reordering)
  async updatePosition(cardId, newPosition, userId) {
    try {
      const { data, error } = await supabase?.from('linked_cards')?.update({ position: newPosition })?.eq('id', cardId)?.eq('user_id', userId)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        position: data?.position
      };
    } catch (error) {
      throw new Error(error?.message || 'Failed to update card position');
    }
  }
};

export default linkedCardService;